<script setup lang="ts">
import { computed } from 'vue';

export type ShipmentTrackerLockedDevice = {
  id: number;
  name: string;
  serial?: string | null;
  imei?: string | null;
};

const props = withDefaults(
  defineProps<{
    devices?: ShipmentTrackerLockedDevice[] | null;
    lockLabel?: string;
  }>(),
  {
    devices: null,
    lockLabel: 'Assigned at creation',
  },
);

const resolvedDevices = computed<ShipmentTrackerLockedDevice[]>(() =>
  props.devices && props.devices.length > 0 ? props.devices : [],
);

function deviceName(device: ShipmentTrackerLockedDevice | null): string {
  const name = device?.name?.trim();
  if (name) return name;
  if (device?.id != null) return `Device #${device.id}`;
  return 'No trackers assigned';
}

function metadataLine(device: ShipmentTrackerLockedDevice): string {
  const parts: string[] = [];
  const serial = device.serial?.trim();
  const imei = device.imei?.trim();
  const id = device.id;

  if (serial) parts.push(`Serial ${serial}`);
  if (imei) parts.push(`IMEI ${imei}`);
  if (id != null) parts.push(`ID ${id}`);

  return parts.join(' · ');
}
</script>

<template>
  <section
    data-testid="shipment-tracker-locked-card"
    class="rounded-lg border border-gray-200 bg-white p-4"
  >
    <h2
      class="mb-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-gpx_gray-200"
    >
      Tracking devices
    </h2>

    <div
      v-if="resolvedDevices.length === 0"
      class="flex items-center gap-4 rounded-lg bg-gpx_gray-900 px-4 py-3"
    >
      <div
        class="flex size-10 shrink-0 items-center justify-center rounded-full bg-white"
      >
        <i class="pi pi-wifi text-base text-gpx_gray-200" aria-hidden="true" />
      </div>
      <div class="min-w-0 flex-1">
        <p class="truncate text-sm font-semibold text-gpx_black-500">
          {{ deviceName(null) }}
        </p>
      </div>
    </div>

    <div v-else class="space-y-2">
      <div
        v-for="device in resolvedDevices"
        :key="device.id"
        class="flex items-center gap-4 rounded-lg bg-gpx_gray-900 px-4 py-3"
      >
        <div
          class="flex size-10 shrink-0 items-center justify-center rounded-full bg-white"
        >
          <i
            class="pi pi-wifi text-base text-gpx_gray-200"
            aria-hidden="true"
          />
        </div>

        <div class="min-w-0 flex-1">
          <p class="truncate text-sm font-semibold text-gpx_black-500">
            {{ deviceName(device) }}
          </p>
          <p
            v-if="metadataLine(device)"
            class="mt-0.5 truncate text-xs text-gpx_gray-200"
          >
            {{ metadataLine(device) }}
          </p>
        </div>

        <div
          class="flex shrink-0 items-center gap-1.5 text-xs text-gpx_gray-200"
        >
          <i class="pi pi-lock text-sm text-amber-500" aria-hidden="true" />
          <span class="whitespace-nowrap">{{ lockLabel }}</span>
        </div>
      </div>
    </div>
  </section>
</template>
